import { QueryClient } from '@tanstack/react-query';

import { isApiError } from './api-errors';

/**
 * Defaults chosen for a console that several people in one church office use at the
 * same time: data goes stale quickly, refetches on focus, and never retries a
 * request the server has already answered definitively.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A treasurer entering contributions and a pastor watching the dashboard should
      // not see numbers thirty seconds apart.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // 401 is handled by the api client's refresh; 403, 404 and every 4xx are the
        // server's settled answer. Retrying them wastes the user's time and, on a
        // rate limit, digs the hole deeper.
        if (isApiError(error) && error.status < 500 && error.status !== 408) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // A mutation is a write. Retrying one automatically is how a church ends up
      // with two contributions for one envelope; anything that must survive a retry
      // carries an idempotency key and asks for it explicitly.
      retry: false,
    },
  },
});

/**
 * Query keys are built here so an invalidation cannot miss a cache entry because two
 * files spelled the same key differently. Every key starts with the church, so
 * switching churches cannot show the previous church's rows for a frame.
 */
export const queryKeys = {
  all: (churchId: string | null) => ['church', churchId] as const,
  list: (churchId: string | null, resource: string, params?: Record<string, unknown>) =>
    ['church', churchId, resource, 'list', params ?? {}] as const,
  detail: (churchId: string | null, resource: string, id: string) =>
    ['church', churchId, resource, 'detail', id] as const,
  /** Not church-scoped: the session and the church list exist above any one church. */
  session: () => ['session'] as const,
  myChurches: () => ['my-churches'] as const,
};
